import { useScrollContainer } from '@w/utils-fe'
import { useSuspenseQuery } from '@w/react-query'
import { queryOptions } from '@w/react-query/db'
import { s } from '@w/schema'
import { useUserSpaces } from '../router/authenticated-layout'
import { Badge } from '@w/ui/components/badge'
import { Button } from '@w/ui/components/button'
import { CheckIcon } from '@w/ui/components/icon'
import {
  Progress,
  ProgressIndicator,
  ProgressTrack,
} from '@w/ui/components/progress'
import { Tabs, TabsList, TabsPanel, TabsTab } from '@w/ui/components/tabs'
import { useTranslation } from '../i18n'
import { translationFor, type TranslationLanguage } from './lexicon-model'
import {
  partitionProgress,
  rankBadge,
  type LemmaProgress,
  type LemmaStatus,
  type AdoptedLemmaRow,
} from './vocabulary-progress'

// Module scope (Zod: define schemas once): inline schemas paid construction on every call.
const stringRecordSchema = s.record(s.string(),s.string())
const labelsSchema = s.object({ labels: s.transform(s.string(), value => stringRecordSchema.parse(JSON.parse(value))) })

const ROW_HEIGHT = 56
const OVERSCAN = 8

export function VocabularyProgressList(props: {
  workspaceId: string
  progress: LemmaProgress[]
  translationLanguage: TranslationLanguage
  onOpenWord: (word: string, status: LemmaStatus) => void
}) {
  const { t } = useTranslation('page')
  const partitions = partitionProgress(props.progress)
  const tabs: Array<{ status: LemmaStatus; label: string }> = [
    { status: 'toLearn', label: t('vocabToLearn') },
    { status: 'inProgress', label: t('vocabInProgress') },
    { status: 'learnt', label: t('vocabLearnt') },
  ]
  const countFormatter = new Intl.NumberFormat(undefined)

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-2">
      <h2 className="text-on-surface font-medium tracking-wide uppercase">
        {t('vocabProgressTitle')}
      </h2>
      <Tabs className="min-h-0 flex-1 rounded-0" defaultValue="toLearn">
        <TabsList>
          {tabs.map(({ status, label }) => {
            const count = countFormatter.format(partitions[status].length)
            return (
              <TabsTab
                key={status}
                aria-label={`${label} ${count}`}
                data-testid={`vocab-tab-${status}`}
                value={status}
              >
                {label} <Badge size="xs">{count}</Badge>
              </TabsTab>
            )
          })}
        </TabsList>
        {tabs.map(({ status }) => (
          <TabsPanel className="min-h-0" key={status} value={status}>
            <VirtualRows
              workspaceId={props.workspaceId}
              rows={partitions[status]}
              translationLanguage={props.translationLanguage}
              onOpenWord={props.onOpenWord}
            />
          </TabsPanel>
        ))}
      </Tabs>
    </section>
  )
}

function VirtualRows(props: {
  workspaceId: string
  rows: LemmaProgress[]
  translationLanguage: TranslationLanguage
  onOpenWord: (word: string, status: LemmaStatus) => void
}) {
  const { t } = useTranslation('page')
  const { scrollTop, viewportHeight, onScroll } = useScrollContainer()
  const viewportRows = Math.ceil((viewportHeight * 0.6) / ROW_HEIGHT)
  const start = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN)
  const end = Math.min(props.rows.length, start + viewportRows + OVERSCAN * 2)
  const visibleRows = props.rows.slice(start, end)

  if (props.rows.length === 0) {
    return (
      <p className="text-on-surface-secondary py-3 text-sm">
        {t('vocabEmptyTab')}
      </p>
    )
  }

  return (
    <div
      className="border-separator bg-surface-2 medium:rounded-2xl relative h-full overflow-y-auto md:max-h-[60vh]"
      data-testid="vocabulary-progress-viewport"
      onScroll={onScroll}
    >
      <div
        className="relative"
        style={{ height: props.rows.length * ROW_HEIGHT }}
      >
        {visibleRows.map((row, offset) => (
          <Button
            key={row.word}
            className="border-separator absolute left-0 flex h-14 w-full justify-start rounded-none border-b px-4 text-left"
            data-testid={`vocab-row-${row.word}`}
            semanticRole="secondary"
            style={{
              transform: `translateY(${(start + offset) * ROW_HEIGHT}px)`,
            }}
            variant="text"
            onClick={() => {
              props.onOpenWord(row.word, row.status)
            }}
          >
            <span className="flex min-w-0 flex-1 flex-col items-start">
              <span className="text-on-surface font-medium">{row.word}</span>
              {row.adopted ? (
                <span className="text-on-surface-secondary truncate text-xs">
                  <AdoptedLabel workspaceId={props.workspaceId} adopted={row.adopted} translationLanguage={props.translationLanguage} />
                </span>
              ) : null}
            </span>
            <span className="flex items-center gap-2">
              <Badge size="xs" semanticRole="secondary">
                {rankBadge(row.rank)}
              </Badge>
              {row.status === 'inProgress' ? (
                <>
                  <Progress
                    aria-label={`${row.senseCount}/${row.definitionCount}`}
                    data-testid={`vocab-progress-${row.word}`}
                    max={row.definitionCount}
                    size="sm"
                    value={row.senseCount}
                    variant="circular"
                  >
                    <ProgressTrack>
                      <ProgressIndicator />
                    </ProgressTrack>
                  </Progress>
                  <span className="text-on-surface-secondary text-xs tabular-nums">
                    {row.senseCount}/{row.definitionCount}
                  </span>
                </>
              ) : null}
              {row.status === 'learnt' ? (
                <CheckIcon
                  className="text-success"
                  data-testid={`vocab-learnt-check-${row.word}`}
                />
              ) : null}
            </span>
          </Button>
        ))}
      </div>
    </div>
  )
}

/** Document detail belongs only to mounted viewport rows, never tab totals. */
function AdoptedLabel(props: { workspaceId: string; adopted: AdoptedLemmaRow; translationLanguage: TranslationLanguage }) {
  const { db, queryClient } = useUserSpaces().get(props.workspaceId)
  const options = queryOptions(db.query`
    WITH displayed AS MATERIALIZED (
      SELECT ${db.pages.id}, ${db.pages.translations} AS labels
      FROM ${db.pages} WHERE ${db.pages.id.inArray(props.adopted.pageIds ?? [])} AND ${db.pages.deletedAt.isNull()}
      ORDER BY ${db.pages.id}
    )
    SELECT json_group_object(key,value) AS labels FROM displayed, json_each(displayed.labels)`,
    labelsSchema, { staleTime: Infinity })
  const { data } = useSuspenseQuery(options, queryClient)
  return <>{props.adopted.transcription}{' '}{translationFor(data[0]?.labels ?? props.adopted.translations, props.translationLanguage)}</>
}
